import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ArrowLeft, AlertCircle } from "lucide-react";
import PageHeader from "../../../components/PageHeader";
import CompanyWizard from "../../../components/CompanyWizard";
import { showAdminCompany, updateAdminCompany, updateFiscalProfile } from "../../../services/adminCompanies";

const CompanyEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [initialData, setInitialData] = useState(null);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await showAdminCompany(id);
      const c = result.company || {};
      const addr = c.address || {};
      const fp = c.fiscal || {};
      const meta = c.administrativeMetadata || {};
      setInitialData({
        name: c.name || "",
        document: c.document || "",
        email: c.email || "",
        phone: c.phone || "",
        postalCode: addr.postalCode || "",
        street: addr.street || "",
        number: addr.number || "",
        complement: addr.complement || "",
        district: addr.district || "",
        city: addr.city || "",
        state: addr.state || "",
        timezone: c.timezone || "America/Sao_Paulo",
        internalNotes: c.internalNotes || "",
        personType: fp.personType || "legal_entity",
        legalName: fp.legalName || "",
        tradeName: fp.tradeName || "",
        municipalRegistration: fp.municipalRegistration || "",
        stateRegistration: fp.stateRegistration || "",
        taxRegime: fp.taxRegime || "",
        cnae: fp.cnae || "",
        fiscalEmail: fp.fiscalEmail || "",
        responsibleName: meta.responsibleName || "",
        responsibleEmail: meta.responsibleEmail || "",
        responsiblePhone: meta.responsiblePhone || ""
      });
    } catch (err) {
      if (err?.response?.status === 404 || err?.message?.includes("404")) {
        setError("Empresa nao encontrada.");
      } else {
        setError(err.message || "Nao foi possivel carregar os dados da empresa.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (form) => {
    setSubmitError("");
    const companyPayload = {
      name: form.name,
      email: form.email || undefined,
      phone: form.phone || undefined,
      document: form.document || undefined,
      timezone: form.timezone || undefined,
      internalNotes: form.internalNotes || undefined,
      administrativeMetadata: {
        responsibleName: form.responsibleName || undefined,
        responsibleEmail: form.responsibleEmail || undefined,
        responsiblePhone: form.responsiblePhone || undefined
      }
    };

    try {
      await updateAdminCompany(id, companyPayload);
    } catch (err) {
      setSubmitError(err.message || "Não foi possível salvar os dados da empresa");
      return;
    }

    const fiscalPayload = {
      postalCode: form.postalCode || undefined,
      street: form.street || undefined,
      number: form.number || undefined,
      complement: form.complement || undefined,
      district: form.district || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      personType: form.personType || undefined,
      legalName: form.legalName || undefined,
      tradeName: form.tradeName || undefined,
      municipalRegistration: form.municipalRegistration || undefined,
      stateRegistration: form.stateRegistration || undefined,
      taxRegime: form.taxRegime || undefined,
      cnae: form.cnae || undefined,
      fiscalEmail: form.fiscalEmail || undefined
    };

    try {
      await updateFiscalProfile(id, fiscalPayload);
    } catch (err) {
      setSubmitError("Os dados principais foram salvos, mas não foi possível salvar os dados fiscais.");
      return;
    }

    try {
      history.push(`/companies/${id}`);
    } catch (err) {
      console.error("Erro ao navegar após salvar:", err);
      setSubmitError("Dados salvos, mas houve um problema ao navegar para a página.");
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumb="Clientes & Acessos"
        title={`Editar Empresa #${id}`}
        subtitle={initialData?.name}
        action={
          <button onClick={() => history.push(`/companies/${id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl">
            <ArrowLeft size={14} /> Voltar
          </button>
        }
      />

      {submitError && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={16} /> {submitError}
        </div>
      )}

      <CompanyWizard
        initialData={initialData}
        loading={loading}
        error={error}
        onSubmit={handleSubmit}
        submitLabel="Salvar"
        backPath={`/companies/${id}`}
      />
    </div>
  );
};

export default CompanyEdit;
